<template>
  <div class="customer-care">
    <div v-if="loading" class="care-state">
      <v-progress-circular indeterminate color="primary" :size="48" :width="5" />
      <strong>Kundenbetreuung wird ausgewertet</strong>
      <span>Termine und dokumentierte Kontakte werden zusammengeführt.</span>
    </div>

    <div v-else-if="error" class="care-state care-state--error">
      <v-icon color="error" size="34">mdi-alert-circle-outline</v-icon>
      <strong>Betreuungsverlauf konnte nicht geladen werden</strong>
      <span>{{ error }}</span>
      <v-btn color="primary" depressed class="text-none mt-2" @click="loadCareData"> Erneut versuchen </v-btn>
    </div>

    <template v-else>
      <section class="care-intro">
        <div>
          <span class="care-eyebrow">KUNDENBETREUUNG</span>
          <h2>Die Kundenreise auf einen Blick</h2>
          <p>
            Termine, Betreuungsteam, Nicht-gekommen-Fälle und dokumentierte Kontakte aus
            {{ data.period ? data.period.historyMonths : 24 }} Monaten.
          </p>
        </div>
        <div class="care-intro__actions">
          <span v-if="data.truncated" class="care-limit">
            <v-icon small>mdi-information-outline</v-icon>
            Sehr viele Einträge – neueste Datensätze angezeigt
          </span>
          <v-btn icon title="Betreuung neu laden" aria-label="Betreuung neu laden" @click="loadCareData">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </section>

      <section class="care-kpis">
        <article v-for="card in summaryCards" :key="card.key" :style="{ '--care-accent': card.color }">
          <span class="care-kpi__icon"
            ><v-icon :color="card.color">{{ card.icon }}</v-icon></span
          >
          <div>
            <div class="care-kpi__label">
              <small>{{ card.label }}</small>
              <v-tooltip v-if="card.info" bottom max-width="360">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" small class="care-kpi__info" :aria-label="`Information zu ${card.label}`" v-on="on">
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>{{ card.info }}</span>
              </v-tooltip>
            </div>
            <strong>{{ card.value }}</strong>
            <p>{{ card.detail }}</p>
          </div>
        </article>
      </section>

      <section v-if="attentionMessage" class="care-attention" :class="`care-attention--${attentionMessage.tone}`">
        <v-icon>{{ attentionMessage.icon }}</v-icon>
        <div>
          <strong>{{ attentionMessage.title }}</strong>
          <span>{{ attentionMessage.text }}</span>
        </div>
      </section>

      <div class="care-grid">
        <article class="care-panel care-panel--journey">
          <header class="care-panel__header">
            <div>
              <span class="care-eyebrow">KUNDENREISE</span>
              <h3>Wichtige Kontaktpunkte</h3>
            </div>
            <v-icon color="#55ca82">mdi-map-marker-path</v-icon>
          </header>
          <div class="journey-list">
            <div v-for="entry in journeyHighlights" :key="entry.key" class="journey-entry">
              <span class="journey-entry__marker" :style="{ '--marker-color': entry.color }">
                <v-icon small :color="entry.color">{{ entry.icon }}</v-icon>
              </span>
              <div>
                <small>{{ entry.label }}</small>
                <strong>{{ entry.value }}</strong>
                <p>{{ entry.detail }}</p>
              </div>
            </div>
          </div>
        </article>

        <article class="care-panel">
          <header class="care-panel__header">
            <div>
              <span class="care-eyebrow">BETREUUNGSTEAM</span>
              <h3>Wer die Person betreut hat</h3>
            </div>
            <span class="care-panel__count">{{ teamRows.length }}</span>
          </header>
          <div v-if="teamRows.length" class="team-list">
            <div v-for="employee in teamRows.slice(0, 8)" :key="employee.key" class="team-entry">
              <v-avatar size="38" color="#27312b">
                <img v-if="personImage(employee.person)" :src="personImage(employee.person)" :alt="employee.name" />
                <span v-else>{{ initials(employee.name) }}</span>
              </v-avatar>
              <div class="team-entry__copy">
                <strong>{{ employee.name }}</strong>
                <span>{{ employee.appointments }} Termine · {{ employee.interactions }} Kontakte</span>
              </div>
              <div class="team-entry__value">
                <strong>{{ formatHours(employee.minutes) }}</strong>
                <small>{{ formatDate(employee.lastDate) }}</small>
              </div>
            </div>
          </div>
          <div v-else class="care-empty">
            <v-icon>mdi-account-question-outline</v-icon>
            <span>Noch kein Betreuungsteam erkennbar.</span>
          </div>
        </article>

        <article class="care-panel">
          <header class="care-panel__header">
            <div>
              <span class="care-eyebrow">TERMINMIX</span>
              <h3>Welche Betreuung genutzt wurde</h3>
            </div>
            <span class="care-panel__count">{{ typeRows.length }}</span>
          </header>
          <div v-if="typeRows.length" class="mix-list">
            <div v-for="type in typeRows.slice(0, 8)" :key="type.key" class="mix-entry">
              <div class="mix-entry__top">
                <strong>{{ appointmentTypeLabel(type.key, type.title) }}</strong>
                <span>{{ type.count }}× · {{ formatHours(type.minutes) }}</span>
              </div>
              <div class="mix-entry__bar">
                <i :style="{ width: `${type.share}%` }"></i>
              </div>
              <small v-if="type.noShows">{{ type.noShows }} nicht gekommen</small>
            </div>
          </div>
          <div v-else class="care-empty">
            <v-icon>mdi-calendar-blank-outline</v-icon>
            <span>Noch keine Kundentermine im Zeitraum.</span>
          </div>
        </article>

        <article class="care-panel">
          <header class="care-panel__header">
            <div>
              <span class="care-eyebrow">AKTIVITÄT</span>
              <h3>Termine der letzten 12 Monate</h3>
            </div>
            <span class="care-panel__count">{{ monthlyTotal }}</span>
          </header>
          <div class="month-chart" :aria-label="`${monthlyTotal} Termine in den letzten zwölf Monaten`">
            <div v-for="month in monthlyRows" :key="month.key" class="month-column">
              <div class="month-column__bars">
                <i
                  class="month-column__bar month-column__bar--active"
                  :style="{ height: `${month.activeHeight}%` }"
                  :title="`${month.active} aktive Termine`"></i>
                <i
                  class="month-column__bar month-column__bar--noshow"
                  :style="{ height: `${month.noShowHeight}%` }"
                  :title="`${month.noShows} nicht gekommen`"></i>
              </div>
              <span>{{ month.label }}</span>
            </div>
          </div>
          <div class="month-legend">
            <span><i class="month-legend__active"></i> Im Kalender betreut</span>
            <span><i class="month-legend__noshow"></i> Nicht gekommen</span>
          </div>
        </article>

        <article class="care-panel care-panel--timeline">
          <header class="care-panel__header care-panel__header--timeline">
            <div>
              <span class="care-eyebrow">CHRONOLOGIE</span>
              <h3>Termine und dokumentierte Kontakte</h3>
            </div>
            <v-chip-group v-model="timelineFilter" mandatory active-class="care-filter--active">
              <v-chip v-for="filter in timelineFilters" :key="filter.value" :value="filter.value" small class="care-filter">
                {{ filter.text }}
              </v-chip>
            </v-chip-group>
          </header>

          <div v-if="filteredTimeline.length" class="care-timeline">
            <div v-for="entry in filteredTimeline.slice(0, visibleTimelineItems)" :key="entry.key" class="timeline-entry">
              <span class="timeline-entry__icon" :class="`timeline-entry__icon--${entryTone(entry)}`">
                <v-icon small>{{ entryIcon(entry) }}</v-icon>
              </span>
              <div class="timeline-entry__date">
                <strong>{{ formatDate(entry.date) }}</strong>
                <span>{{ entry.start || entry.location || entrySourceLabel(entry) }}</span>
              </div>
              <div class="timeline-entry__main">
                <div>
                  <strong>{{ entryTitle(entry) }}</strong>
                  <span v-if="entry.employee">{{ personName(entry.employee) }}</span>
                  <span v-if="entry.studio && entry.studio.name">{{ entry.studio.name }}</span>
                </div>
                <p v-if="entry.description">{{ entry.description }}</p>
              </div>
              <div class="timeline-entry__status">
                <span :class="`status-pill status-pill--${entryTone(entry)}`">{{ entryStatusLabel(entry) }}</span>
                <small v-if="entry.kind === 'appointment'">{{ entryDurationLabel(entry) }}</small>
              </div>
            </div>

            <v-btn
              v-if="filteredTimeline.length > visibleTimelineItems"
              text
              color="primary"
              class="text-none timeline-more"
              @click="visibleTimelineItems += 20">
              Weitere {{ Math.min(20, filteredTimeline.length - visibleTimelineItems) }} anzeigen
              <v-icon right small>mdi-chevron-down</v-icon>
            </v-btn>
          </div>
          <div v-else class="care-empty care-empty--large">
            <v-icon>mdi-timeline-clock-outline</v-icon>
            <strong>Keine passenden Einträge</strong>
            <span>Für diesen Filter ist im ausgewählten Zeitraum nichts vorhanden.</span>
          </div>
        </article>
      </div>
    </template>
  </div>
</template>

<script>
import api from "../../../api"

const TYPE_LABELS = {
  trial: "Probetraining",
  intro: "Einführungstraining",
  followup: "Folgetermin",
  follow: "Folgetermin",
  check: "Check-up",
  milon: "Milon-Training",
  nutrition: "Ernährungsberatung",
  consultation: "Beratungsgespräch",
  workout: "Training",
  trainingplan: "Trainingsplan",
  anamnese: "Anamnese / Check-up",
  new_contract: "Vertragserstellung",
  upgrade: "Vertrag / Upgrade",
  first: "Erstberatung",
  normal: "Termin",
  legacy: "TAC-Termin",
}

export default {
  name: "UserDashboardCustomerCare",
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      error: "",
      data: {
        period: null,
        appointments: [],
        interactions: [],
        truncated: false,
      },
      timelineFilter: "all",
      visibleTimelineItems: 20,
      timelineFilters: [
        { text: "Alle", value: "all" },
        { text: "Termine", value: "appointment" },
        { text: "Kontakte", value: "interaction" },
        { text: "Nicht gekommen", value: "noshow" },
      ],
    }
  },
  computed: {
    normalizedAppointments() {
      return (this.data.appointments || []).map((entry) => {
        const dateKey = this.$moment(entry.date).isValid() ? this.$moment(entry.date).format("YYYY-MM-DD") : ""
        const startsAt = this.$moment(`${dateKey} ${entry.start || "00:00"}`, "YYYY-MM-DD HH:mm")
        let minutes = 0
        if (entry.start && entry.end) {
          const end = this.$moment(`${dateKey} ${entry.end}`, "YYYY-MM-DD HH:mm")
          if (startsAt.isValid() && end.isValid()) minutes = Math.max(0, end.diff(startsAt, "minutes"))
        }
        const status = String(entry.status || "planned").toLowerCase()
        const isNoShow = status === "noshow" || status === "no_show"
        const isCancelled = status === "cancelled" || status === "canceled"
        const isPast = startsAt.isValid() && startsAt.isBefore(this.$moment())
        return {
          ...entry,
          key: `appointment:${entry._id}`,
          dateKey,
          startsAt,
          minutes,
          status,
          isNoShow,
          isCancelled,
          isPast,
          isUpcoming: !isPast && !isCancelled && !isNoShow,
          isProductive: isPast && !isCancelled && !isNoShow,
        }
      })
    },
    normalizedInteractions() {
      return (this.data.interactions || []).map((entry) => ({
        ...entry,
        key: `interaction:${entry._id}`,
        kind: "interaction",
      }))
    },
    pastAppointments() {
      return this.normalizedAppointments.filter((entry) => entry.isPast)
    },
    productiveAppointments() {
      return this.normalizedAppointments.filter((entry) => entry.isProductive)
    },
    upcomingAppointments() {
      return this.normalizedAppointments.filter((entry) => entry.isUpcoming).sort((left, right) => left.startsAt.valueOf() - right.startsAt.valueOf())
    },
    noShows() {
      return this.pastAppointments.filter((entry) => entry.isNoShow)
    },
    cancelledAppointments() {
      return this.pastAppointments.filter((entry) => entry.isCancelled)
    },
    relevantPastAppointments() {
      return this.pastAppointments.filter((entry) => !entry.isCancelled)
    },
    appointmentMinutes() {
      return this.productiveAppointments.reduce((sum, entry) => sum + Number(entry.minutes || 0), 0)
    },
    noShowRate() {
      const total = this.relevantPastAppointments.length
      return total ? Math.round((this.noShows.length / total) * 100) : 0
    },
    summaryCards() {
      return [
        {
          key: "appointments",
          label: "Kundentermine",
          value: this.productiveAppointments.length,
          detail: `${this.upcomingAppointments.length} kommende Termine`,
          icon: "mdi-calendar-account-outline",
          color: "#2587e8",
        },
        {
          key: "hours",
          label: "Aktive Terminzeit",
          value: this.formatHours(this.appointmentMinutes),
          detail: "Nur über die App erfasste Termine mit Dauer",
          info: "Historische TAC-Termine können in der Terminanzahl erscheinen. Für die Terminzeit zählen nur Termine, die im neuen App-Terminkalender mit Start und Ende erfasst wurden. Absagen und Nicht-gekommen sind ausgeschlossen.",
          icon: "mdi-timer-sand-complete",
          color: "#16a765",
        },
        {
          key: "no-show",
          label: "Nicht gekommen",
          value: this.noShows.length,
          detail: `${this.noShowRate}% der relevanten Termine`,
          icon: "mdi-account-off-outline",
          color: this.noShows.length ? "#ef4444" : "#55ca82",
        },
        {
          key: "contacts",
          label: "Dokumentierte Kontakte",
          value: this.normalizedInteractions.length,
          detail: `${this.teamRows.length} betreuende Mitarbeitende`,
          icon: "mdi-account-heart-outline",
          color: "#a855f7",
        },
      ]
    },
    nextAppointment() {
      return this.upcomingAppointments[0] || null
    },
    firstAppointment() {
      return [...this.productiveAppointments].sort((left, right) => left.startsAt.valueOf() - right.startsAt.valueOf())[0] || null
    },
    lastAppointment() {
      return [...this.productiveAppointments].sort((left, right) => right.startsAt.valueOf() - left.startsAt.valueOf())[0] || null
    },
    attentionMessage() {
      if (this.noShows.length >= 2 && this.noShowRate >= 15) {
        return {
          tone: "warning",
          icon: "mdi-account-alert-outline",
          title: "Wiederholte Nicht-gekommen-Fälle",
          text: `${this.noShows.length} Fälle (${this.noShowRate}%). Die Notizen in der Chronologie helfen bei der nächsten Kontaktaufnahme.`,
        }
      }
      if (this.nextAppointment) {
        return {
          tone: "good",
          icon: "mdi-calendar-check-outline",
          title: "Nächste Betreuung ist geplant",
          text: `${this.formatDate(this.nextAppointment.date)} ${this.nextAppointment.start || ""} · ${this.entryTitle(this.nextAppointment)}`,
        }
      }
      if (this.lastAppointment) {
        const days = Math.max(0, this.$moment().startOf("day").diff(this.lastAppointment.startsAt.clone().startOf("day"), "days"))
        if (days >= 90) {
          return {
            tone: "neutral",
            icon: "mdi-calendar-search-outline",
            title: "Seit längerer Zeit kein Kundentermin",
            text: `Der letzte aktive Termin war vor ${days} Tagen am ${this.formatDate(this.lastAppointment.date)}.`,
          }
        }
      }
      return null
    },
    journeyHighlights() {
      return [
        {
          key: "first",
          label: "Erster Termin im Zeitraum",
          value: this.firstAppointment ? this.formatDate(this.firstAppointment.date) : "Noch keiner",
          detail: this.firstAppointment ? this.entryTitle(this.firstAppointment) : "Keine Terminhistorie vorhanden",
          icon: "mdi-flag-variant-outline",
          color: "#2587e8",
        },
        {
          key: "last",
          label: "Letzte aktive Betreuung",
          value: this.lastAppointment ? this.formatDate(this.lastAppointment.date) : "Noch keine",
          detail: this.lastAppointment ? this.personName(this.lastAppointment.employee) || this.entryTitle(this.lastAppointment) : "–",
          icon: "mdi-history",
          color: "#16a765",
        },
        {
          key: "next",
          label: "Nächster Termin",
          value: this.nextAppointment ? this.formatDate(this.nextAppointment.date) : "Nichts geplant",
          detail: this.nextAppointment
            ? `${this.nextAppointment.start || ""} · ${this.entryTitle(this.nextAppointment)}`
            : "Aktuell kein kommender Kundentermin",
          icon: "mdi-calendar-arrow-right",
          color: "#f59e0b",
        },
        {
          key: "cancelled",
          label: "Abgesagte Termine",
          value: this.cancelledAppointments.length,
          detail: "im ausgewerteten Zeitraum",
          icon: "mdi-calendar-remove-outline",
          color: "#94a3b8",
        },
      ]
    },
    teamRows() {
      const team = new Map()
      const ensure = (person) => {
        const name = this.personName(person)
        if (!name) return null
        const key = name.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase()
        if (!team.has(key)) {
          team.set(key, {
            key,
            name,
            person,
            appointments: 0,
            interactions: 0,
            minutes: 0,
            lastDate: null,
          })
        } else if (!team.get(key).person?._id && person?._id) {
          team.get(key).person = person
        }
        return team.get(key)
      }

      this.productiveAppointments.forEach((entry) => {
        const employee = ensure(entry.employee)
        if (!employee) return
        employee.appointments += 1
        employee.minutes += Number(entry.minutes || 0)
        if (!employee.lastDate || this.$moment(entry.date).isAfter(employee.lastDate)) employee.lastDate = entry.date
      })
      this.normalizedInteractions.forEach((entry) => {
        const employee = ensure(entry.employee)
        if (!employee) return
        employee.interactions += 1
        if (!employee.lastDate || this.$moment(entry.date).isAfter(employee.lastDate)) employee.lastDate = entry.date
      })

      return [...team.values()].sort(
        (left, right) => right.appointments + right.interactions - (left.appointments + left.interactions) || right.minutes - left.minutes,
      )
    },
    typeRows() {
      const types = new Map()
      this.pastAppointments.forEach((entry) => {
        const key = String(entry.appointmentType || entry.title || "normal").toLowerCase()
        if (!types.has(key)) {
          types.set(key, {
            key,
            title: entry.title || "Termin",
            count: 0,
            noShows: 0,
            minutes: 0,
          })
        }
        const type = types.get(key)
        if (!entry.isCancelled) type.count += 1
        if (entry.isNoShow) type.noShows += 1
        if (entry.isProductive) type.minutes += Number(entry.minutes || 0)
      })
      const rows = [...types.values()].filter((type) => type.count > 0).sort((left, right) => right.count - left.count)
      const max = Math.max(1, ...rows.map((type) => type.count))
      return rows.map((type) => ({ ...type, share: Math.max(4, Math.round((type.count / max) * 100)) }))
    },
    monthlyRows() {
      const months = []
      for (let offset = 11; offset >= 0; offset -= 1) {
        const date = this.$moment().subtract(offset, "months")
        months.push({
          key: date.format("YYYY-MM"),
          label: date.format("MMM").replace(".", ""),
          active: 0,
          noShows: 0,
        })
      }
      const lookup = Object.fromEntries(months.map((month) => [month.key, month]))
      this.pastAppointments.forEach((entry) => {
        const month = lookup[this.$moment(entry.date).format("YYYY-MM")]
        if (!month) return
        if (entry.isNoShow) month.noShows += 1
        else if (!entry.isCancelled) month.active += 1
      })
      const max = Math.max(1, ...months.map((month) => month.active + month.noShows))
      return months.map((month) => ({
        ...month,
        activeHeight: Math.round((month.active / max) * 100),
        noShowHeight: Math.round((month.noShows / max) * 100),
      }))
    },
    monthlyTotal() {
      return this.monthlyRows.reduce((sum, month) => sum + month.active + month.noShows, 0)
    },
    timelineRows() {
      return [...this.normalizedAppointments, ...this.normalizedInteractions].sort(
        (left, right) => this.$moment(right.date).valueOf() - this.$moment(left.date).valueOf(),
      )
    },
    filteredTimeline() {
      if (this.timelineFilter === "appointment") return this.timelineRows.filter((entry) => entry.kind === "appointment")
      if (this.timelineFilter === "interaction") return this.timelineRows.filter((entry) => entry.kind === "interaction")
      if (this.timelineFilter === "noshow") return this.timelineRows.filter((entry) => entry.isNoShow)
      return this.timelineRows
    },
  },
  watch: {
    "user._id": {
      immediate: true,
      handler(userId) {
        if (userId) this.loadCareData()
      },
    },
    timelineFilter() {
      this.visibleTimelineItems = 20
    },
  },
  methods: {
    async loadCareData() {
      if (!this.user || !this.user._id) return
      this.loading = true
      this.error = ""
      try {
        this.data = await api.getCustomerCare(this.user._id, 24)
      } catch (error) {
        this.error = error?.response?.data?.message || "Bitte Verbindung und Zugriffsrechte prüfen."
      } finally {
        this.loading = false
        this.$emit("loaded")
      }
    },
    personName(person) {
      if (!person) return ""
      return `${person.vorname || ""} ${person.nachname || ""}`.trim()
    },
    personImage(person) {
      if (!person) return ""
      if (person.bild) return `/img/profilbilder${person.bild}`
      if (person._id) return `https://app.wellcomefit.ch/api/checkin/pool/images/${person._id}`
      return ""
    },
    initials(name) {
      return String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase()
    },
    formatDate(value) {
      if (!value || !this.$moment(value).isValid()) return "–"
      return this.$moment(value).format("DD.MM.YY")
    },
    formatHours(minutes) {
      const value = Number(minutes || 0) / 60
      return `${value.toFixed(value >= 10 ? 0 : 1)}h`
    },
    appointmentTypeLabel(key, fallback) {
      return TYPE_LABELS[String(key || "").toLowerCase()] || fallback || "Termin"
    },
    entryTitle(entry) {
      if (!entry) return "Termin"
      if (entry.kind === "interaction") return entry.title || "Dokumentierter Kontakt"
      return this.appointmentTypeLabel(entry.appointmentType, entry.title)
    },
    entryDurationLabel(entry) {
      if (!entry || !entry.minutes) return "Dauer nicht erfasst"
      if (entry.minutes < 60) return `${entry.minutes} Min.`
      const hours = entry.minutes / 60
      return `${hours.toFixed(hours % 1 ? 1 : 0)}h`
    },
    entryTone(entry) {
      if (entry.kind === "interaction") return "contact"
      if (entry.isNoShow) return "danger"
      if (entry.isCancelled) return "muted"
      if (entry.isUpcoming) return "upcoming"
      return "success"
    },
    entryIcon(entry) {
      if (entry.kind === "interaction") return "mdi-message-text-clock-outline"
      if (entry.isNoShow) return "mdi-account-off-outline"
      if (entry.isCancelled) return "mdi-calendar-remove-outline"
      if (entry.isUpcoming) return "mdi-calendar-arrow-right"
      return "mdi-calendar-check-outline"
    },
    entryStatusLabel(entry) {
      if (entry.kind === "interaction") return "Kontakt"
      if (entry.isNoShow) return "Nicht gekommen"
      if (entry.isCancelled) return "Abgesagt"
      if (entry.isUpcoming) return "Geplant"
      return "Im Kalender"
    },
    entrySourceLabel(entry) {
      if (entry.source === "tac") return "TAC"
      if (entry.kind === "interaction") return "Kontakt"
      return "WorkCalendar"
    },
  },
}
</script>

<style scoped>
.customer-care {
  --care-bg: rgba(20, 22, 23, 0.94);
  --care-surface: rgba(255, 255, 255, 0.035);
  --care-surface-raised: rgba(255, 255, 255, 0.055);
  --care-border: rgba(255, 255, 255, 0.1);
  --care-text: #f7f8f8;
  --care-muted: #a8ada9;
  width: 100%;
  color: var(--care-text);
}

.care-state {
  display: flex;
  min-height: 340px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  border: 1px solid var(--care-border);
  border-radius: 22px;
  background: var(--care-surface);
  text-align: center;
}

.care-state strong {
  font-size: 17px;
}

.care-state span {
  color: var(--care-muted);
  font-size: 13px;
}

.care-state--error {
  border-color: rgba(239, 68, 68, 0.28);
}

.care-intro {
  display: flex;
  min-height: 124px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 26px;
  border: 1px solid var(--care-border);
  border-radius: 22px;
  background: radial-gradient(circle at 92% 0%, rgba(0, 150, 64, 0.18), transparent 35%),
    linear-gradient(135deg, rgba(0, 150, 64, 0.08), rgba(255, 255, 255, 0.025));
}

.care-eyebrow {
  display: block;
  margin-bottom: 5px;
  color: #65ca8b;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0.12em;
  line-height: 1.2;
}

.care-intro h2,
.care-panel h3 {
  margin: 0;
  color: #ffffff;
  letter-spacing: -0.025em;
}

.care-intro h2 {
  font-size: clamp(23px, 2.3vw, 31px);
}

.care-intro p {
  max-width: 720px;
  margin: 8px 0 0;
  color: var(--care-muted);
  font-size: 14px;
  line-height: 1.55;
}

.care-intro__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.care-intro__actions .v-btn .v-icon {
  color: #d8ded9 !important;
}

.care-limit {
  display: inline-flex;
  max-width: 300px;
  align-items: center;
  gap: 7px;
  padding: 8px 11px;
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 10px;
  color: #f3c568;
  background: rgba(245, 158, 11, 0.09);
  font-size: 12px;
}

.care-kpis {
  display: grid;
  margin-top: 14px;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.care-kpis article {
  position: relative;
  display: flex;
  min-height: 132px;
  gap: 14px;
  padding: 19px;
  overflow: hidden;
  border: 1px solid var(--care-border);
  border-radius: 19px;
  background: linear-gradient(145deg, var(--care-surface-raised), rgba(0, 0, 0, 0.14));
}

.care-kpis article::after {
  position: absolute;
  right: -34px;
  bottom: -48px;
  width: 112px;
  height: 112px;
  border-radius: 50%;
  background: var(--care-accent);
  content: "";
  opacity: 0.07;
}

.care-kpi__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: color-mix(in srgb, var(--care-accent) 14%, transparent);
}

.care-kpi__icon .v-icon {
  color: var(--care-accent) !important;
}

.care-kpis small {
  display: block;
  color: var(--care-muted);
  font-size: 12px;
  font-weight: 750;
}

.care-kpis strong {
  display: block;
  margin-top: 5px;
  color: #ffffff;
  font-size: 29px;
  letter-spacing: -0.04em;
  line-height: 1;
}

.care-kpis p {
  margin: 8px 0 0;
  color: #b8bdb9;
  font-size: 12px;
  line-height: 1.35;
}

.care-attention {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-top: 14px;
  padding: 15px 18px;
  border: 1px solid;
  border-radius: 16px;
}

.care-attention > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.care-attention strong {
  font-size: 14px;
}

.care-attention span {
  font-size: 12px;
}

.care-attention--warning {
  border-color: rgba(239, 68, 68, 0.25);
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.08);
}

.care-attention--good {
  border-color: rgba(22, 167, 101, 0.28);
  color: #85dca9;
  background: rgba(22, 167, 101, 0.09);
}

.care-attention--neutral {
  border-color: rgba(245, 158, 11, 0.25);
  color: #f3c568;
  background: rgba(245, 158, 11, 0.08);
}

.care-attention .v-icon {
  color: inherit !important;
}

.care-grid {
  display: grid;
  margin-top: 14px;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.care-panel {
  min-width: 0;
  padding: 22px;
  border: 1px solid var(--care-border);
  border-radius: 21px;
  background: linear-gradient(150deg, var(--care-surface), rgba(0, 0, 0, 0.13));
}

.care-panel--timeline {
  grid-column: 1 / -1;
}

.care-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
}

.care-panel__header > .v-icon {
  color: #65ca8b !important;
}

.care-panel h3 {
  font-size: 18px;
}

.care-panel__count {
  display: inline-flex;
  min-width: 34px;
  height: 28px;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 999px;
  color: #7bd49e;
  background: rgba(0, 150, 64, 0.1);
  font-size: 12px;
  font-weight: 800;
}

.journey-list {
  display: grid;
  gap: 3px;
}

.journey-entry {
  position: relative;
  display: grid;
  min-height: 72px;
  gap: 13px;
  grid-template-columns: 38px minmax(0, 1fr);
}

.journey-entry:not(:last-child)::after {
  position: absolute;
  top: 38px;
  bottom: -3px;
  left: 18px;
  width: 1px;
  background: rgba(255, 255, 255, 0.1);
  content: "";
}

.journey-entry__marker {
  z-index: 1;
  display: inline-flex;
  width: 37px;
  height: 37px;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--marker-color) 28%, transparent);
  border-radius: 12px;
  background: #202422;
}

.journey-entry__marker .v-icon {
  color: var(--marker-color) !important;
}

.journey-entry small,
.team-entry span,
.team-entry small,
.mix-entry small {
  color: var(--care-muted);
  font-size: 12px;
}

.journey-entry strong {
  display: block;
  margin-top: 2px;
  color: #ffffff;
  font-size: 15px;
}

.journey-entry p {
  margin: 2px 0 0;
  color: #b6bbb7;
  font-size: 12px;
}

.team-list {
  display: grid;
  gap: 7px;
}

.team-entry {
  display: grid;
  align-items: center;
  gap: 11px;
  padding: 9px 10px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.025);
  grid-template-columns: 38px minmax(0, 1fr) auto;
}

.team-entry__copy,
.team-entry__value {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.team-entry__copy strong {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-entry__value {
  align-items: flex-end;
}

.team-entry__value strong {
  color: #7bd49e;
  font-size: 14px;
}

.mix-list {
  display: grid;
  gap: 13px;
}

.mix-entry__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.mix-entry__top strong {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mix-entry__top span {
  flex: 0 0 auto;
  color: #c4c8c5;
  font-size: 12px;
}

.mix-entry__bar {
  height: 6px;
  margin-top: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
}

.mix-entry__bar i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #009640, #55ca82);
}

.mix-entry > small {
  display: block;
  margin-top: 4px;
  color: #fca5a5;
}

.month-chart {
  display: grid;
  min-height: 184px;
  align-items: flex-end;
  gap: 5px;
  grid-template-columns: repeat(12, minmax(0, 1fr));
}

.month-column {
  display: grid;
  height: 170px;
  align-items: flex-end;
  gap: 8px;
  grid-template-rows: minmax(0, 1fr) auto;
  text-align: center;
}

.month-column__bars {
  display: flex;
  height: 142px;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
}

.month-column__bar {
  display: block;
  min-height: 3px;
  width: min(12px, 42%);
  border-radius: 5px 5px 2px 2px;
  transition: height 220ms ease;
}

.month-column__bar--active {
  background: linear-gradient(180deg, #55ca82, #009640);
}

.month-column__bar--noshow {
  background: #ef4444;
}

.month-column > span {
  color: var(--care-muted);
  font-size: 12px;
  text-transform: uppercase;
}

.month-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 12px;
  color: var(--care-muted);
  font-size: 12px;
}

.month-legend span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.month-legend i {
  width: 9px;
  height: 9px;
  border-radius: 3px;
}

.month-legend__active {
  background: #16a765;
}

.month-legend__noshow {
  background: #ef4444;
}

.care-panel__header--timeline {
  align-items: flex-start;
}

.care-filter {
  min-height: 30px !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  color: #b7bbb8 !important;
  background: rgba(255, 255, 255, 0.035) !important;
  font-size: 12px !important;
}

.care-filter--active {
  border-color: rgba(0, 150, 64, 0.35) !important;
  color: #83dba5 !important;
  background: rgba(0, 150, 64, 0.14) !important;
}

.care-timeline {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 16px;
}

.timeline-entry {
  display: grid;
  min-height: 78px;
  align-items: center;
  gap: 13px;
  padding: 11px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.065);
  grid-template-columns: 38px 112px minmax(0, 1fr) auto;
}

.timeline-entry:last-of-type {
  border-bottom: 0;
}

.timeline-entry__icon {
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
}

.timeline-entry__icon--success {
  color: #7bd49e;
  background: rgba(22, 167, 101, 0.13);
}

.timeline-entry__icon--upcoming {
  color: #71b8f3;
  background: rgba(37, 135, 232, 0.13);
}

.timeline-entry__icon--danger {
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.13);
}

.timeline-entry__icon--muted {
  color: #aab1ba;
  background: rgba(148, 163, 184, 0.12);
}

.timeline-entry__icon--contact {
  color: #d8b4fe;
  background: rgba(168, 85, 247, 0.13);
}

.timeline-entry__icon .v-icon {
  color: inherit !important;
}

.timeline-entry__date,
.timeline-entry__main,
.timeline-entry__status {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.timeline-entry__date strong,
.timeline-entry__main strong {
  color: #f5f7f6;
  font-size: 13px;
}

.timeline-entry__date span,
.timeline-entry__main span,
.timeline-entry__status small {
  color: var(--care-muted);
  font-size: 12px;
}

.timeline-entry__main > div {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 5px 12px;
}

.timeline-entry__main p {
  display: -webkit-box;
  overflow: hidden;
  margin: 5px 0 0;
  color: #c0c4c1;
  font-size: 12px;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.timeline-entry__status {
  align-items: flex-end;
  gap: 5px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 750;
  white-space: nowrap;
}

.status-pill--success {
  color: #7bd49e;
  background: rgba(22, 167, 101, 0.12);
}

.status-pill--upcoming {
  color: #71b8f3;
  background: rgba(37, 135, 232, 0.12);
}

.status-pill--danger {
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.12);
}

.status-pill--muted {
  color: #b1b8c0;
  background: rgba(148, 163, 184, 0.11);
}

.status-pill--contact {
  color: #d8b4fe;
  background: rgba(168, 85, 247, 0.12);
}

.timeline-more {
  width: 100%;
  min-height: 44px;
  border-radius: 0 !important;
}

.care-empty {
  display: flex;
  min-height: 150px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--care-muted);
  text-align: center;
}

.care-empty .v-icon {
  color: #89928c !important;
}

.care-empty span,
.care-empty strong {
  font-size: 13px;
}

.care-empty--large {
  min-height: 240px;
}

@media (max-width: 1100px) {
  .care-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .care-grid {
    grid-template-columns: 1fr;
  }

  .care-panel--timeline {
    grid-column: auto;
  }
}

@media (max-width: 760px) {
  .care-intro,
  .care-panel__header--timeline {
    flex-direction: column;
    align-items: flex-start;
  }

  .care-intro__actions {
    width: 100%;
    justify-content: space-between;
  }

  .care-kpis {
    grid-template-columns: 1fr;
  }

  .care-kpis article {
    min-height: 112px;
  }

  .care-panel {
    padding: 17px;
  }

  .timeline-entry {
    align-items: flex-start;
    grid-template-columns: 34px minmax(0, 1fr);
  }

  .timeline-entry__date {
    grid-column: 2;
    grid-row: 1;
  }

  .timeline-entry__main {
    grid-column: 2;
  }

  .timeline-entry__status {
    grid-column: 2;
    align-items: flex-start;
  }

  .month-column:nth-child(odd) > span {
    visibility: hidden;
  }
}
</style>
